import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { deliveryAPI, logisticsAPI } from "../utils/api";
import { CheckCircle, XCircle, AlertTriangle, Clock, ChevronLeft, RefreshCw, Check } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9e7e0d6e-4f16-4092-b7c7-18a05794ec67/artifacts/w0k6jgr2_WhatsApp%20Image%202026-04-07%20at%2017.12.34.jpeg";

const STATUS_CYCLE = ["pending", "delivered", "partial", "not_delivered"];
const STATUS_CONFIG = {
  pending:       { label: "Tap to Update",  bg: "bg-[#F4F3F0]",            text: "text-[#8A7E73]",   border: "border-[#E5E1DB]",         icon: Clock,         dot: "bg-[#C4BAAF]" },
  delivered:     { label: "Delivered",       bg: "bg-[#637C64]",            text: "text-white",       border: "border-[#637C64]",         icon: CheckCircle,   dot: "bg-white" },
  partial:       { label: "Partial Delivery",bg: "bg-[#D98032]",            text: "text-white",       border: "border-[#D98032]",         icon: AlertTriangle, dot: "bg-white" },
  not_delivered: { label: "Not Delivered",   bg: "bg-[#C2624E]",            text: "text-white",       border: "border-[#C2624E]",         icon: XCircle,       dot: "bg-white" },
};

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return time;
}

export default function DeliveryKiosk() {
  const navigate = useNavigate();
  const time = useClock();
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(() => localStorage.getItem("kiosk_route") || "");
  const [routeObj, setRouteObj] = useState(null);
  const [date] = useState(new Date().toISOString().split("T")[0]);
  const [statuses, setStatuses] = useState({}); // {schoolName: status}
  const [saving, setSaving] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [showExit, setShowExit] = useState(false);
  const [showRoutePicker, setShowRoutePicker] = useState(false);
  const autoSaveRef = useRef({});

  // Load routes
  useEffect(() => {
    logisticsAPI.getRoutes().then(r => {
      const valid = (r.data || []).filter(r => !r.route_number?.startsWith("TEST"));
      setRoutes(valid);
      if (!selectedRoute && valid.length > 0) {
        setSelectedRoute(valid[0].id);
        localStorage.setItem("kiosk_route", valid[0].id);
      }
    });
  }, []);

  // Load route details + existing statuses
  useEffect(() => {
    if (!selectedRoute) return;
    const route = routes.find(r => r.id === selectedRoute);
    setRouteObj(route || null);
    loadExistingStatuses();
  }, [selectedRoute, routes]);

  const loadExistingStatuses = async () => {
    if (!selectedRoute) return;
    try {
      await deliveryAPI.initRoute({ route_id: selectedRoute, date });
      const r = await deliveryAPI.getStatus({ date, route_id: selectedRoute });
      const s = {};
      (r.data || []).forEach(d => { s[d.school_name] = d.status || "pending"; });
      setStatuses(s);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const cycleStatus = useCallback(async (schoolName) => {
    const current = statuses[schoolName] || "pending";
    const idx = STATUS_CYCLE.indexOf(current);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];

    // Immediate UI update
    setStatuses(prev => ({ ...prev, [schoolName]: next }));
    setSaving(prev => ({ ...prev, [schoolName]: true }));

    // Debounced auto-save
    if (autoSaveRef.current[schoolName]) clearTimeout(autoSaveRef.current[schoolName]);
    autoSaveRef.current[schoolName] = setTimeout(async () => {
      try {
        await deliveryAPI.updateDelivery({
          school_name: schoolName,
          route_id: selectedRoute,
          route_number: routeObj?.route_number || "",
          date,
          kitchen_id: null,
          status: next,
          delivery_time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
        });
        setLastSaved(new Date());
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
      setSaving(prev => ({ ...prev, [schoolName]: false }));
    }, 800);
  }, [statuses, selectedRoute, routeObj, date]);

  const markAllDelivered = async () => {
    const schools = routeObj?.schools || [];
    const updates = {};
    schools.forEach(s => { updates[s.name] = "delivered"; });
    setStatuses(prev => ({ ...prev, ...updates }));
    try {
      await deliveryAPI.bulkUpdate({
        route_id: selectedRoute,
        date,
        kitchen_id: null,
        deliveries: schools.map(s => ({
          school_name: s.name,
          route_id: selectedRoute,
          route_number: routeObj?.route_number || "",
          date,
          status: "delivered",
          delivery_time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
        }))
      });
      setLastSaved(new Date());
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const schools = routeObj?.schools || [];
  const delivered = schools.filter(s => statuses[s.name] === "delivered").length;
  const partial = schools.filter(s => statuses[s.name] === "partial").length;
  const notDel = schools.filter(s => statuses[s.name] === "not_delivered").length;
  const progress = schools.length > 0 ? Math.round((delivered / schools.length) * 100) : 0;

  const pickRoute = (id) => {
    setSelectedRoute(id);
    localStorage.setItem("kiosk_route", id);
    setShowRoutePicker(false);
    setStatuses({});
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-[#FAFAF8] overflow-hidden" data-testid="delivery-kiosk">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E5E1DB] px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Manna" className="w-9 h-9 rounded-lg object-contain flex-shrink-0" />
          <div>
            <p className="font-bold text-[#3A322C] text-sm leading-tight">Delivery Tracking</p>
            <button onClick={() => setShowRoutePicker(true)} className="text-xs text-[#E8956D] font-bold flex items-center gap-1">
              {routeObj ? `Route ${routeObj.route_number}` : "Select Route"} ▾
            </button>
          </div>
        </div>
        <div className="text-center">
          <p className="text-lg font-mono font-bold text-[#3A322C] tabular-nums">
            {time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <p className="text-[10px] text-[#8A7E73]">
            {time.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
          </p>
        </div>
        <button onClick={() => setShowExit(true)} className="text-[#8A7E73] hover:text-[#3A322C] p-2">
          <ChevronLeft size={20} />
        </button>
      </div>

      {/* Progress bar + summary */}
      {schools.length > 0 && (
        <div className="bg-white border-b border-[#E5E1DB] px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex gap-3 text-sm">
              <span className="font-bold text-[#637C64]">{delivered} delivered</span>
              {partial > 0 && <span className="font-bold text-[#D98032]">{partial} partial</span>}
              {notDel > 0 && <span className="font-bold text-[#C2624E]">{notDel} missed</span>}
              <span className="text-[#8A7E73]">of {schools.length}</span>
            </div>
            <div className="flex items-center gap-2">
              {lastSaved && <span className="text-[10px] text-[#8A7E73]">Saved {lastSaved.toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit"})}</span>}
              <button onClick={markAllDelivered} className="text-xs bg-[#637C64] text-white px-3 py-1.5 rounded-lg font-bold hover:bg-[#4e6350]">
                Mark All Delivered
              </button>
            </div>
          </div>
          <div className="w-full bg-[#F4F3F0] rounded-full h-2.5 overflow-hidden">
            <div className="h-2.5 bg-[#637C64] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-[#8A7E73] mt-1">{progress}% complete</p>
        </div>
      )}

      {/* School cards — large tap targets for tablets */}
      <div className="flex-1 overflow-y-auto p-3">
        {!routeObj ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-[#8A7E73] mb-4">Select a route to start tracking deliveries</p>
            <button onClick={() => setShowRoutePicker(true)} className="bg-[#E8956D] text-[#3A322C] rounded-xl px-8 py-4 font-bold text-lg">
              Select Route
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {schools.map(school => {
              const status = statuses[school.name] || "pending";
              const cfg = STATUS_CONFIG[status];
              const Icon = cfg.icon;
              const isSaving = saving[school.name];
              return (
                <button
                  key={school.name}
                  onClick={() => cycleStatus(school.name)}
                  className={`relative ${cfg.bg} border-2 ${cfg.border} rounded-2xl p-4 text-left transition-all duration-200 active:scale-[0.97] min-h-[88px] flex items-center gap-4 shadow-sm`}
                  data-testid={`school-${school.name.replace(/\s+/g,"-").toLowerCase()}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${status === "pending" ? "bg-white" : "bg-white/20"} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={24} className={status === "pending" ? "text-[#C4BAAF]" : "text-white opacity-90"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm leading-tight truncate ${cfg.text}`}>{school.name}</p>
                    <p className={`text-xs mt-0.5 ${status === "pending" ? "text-[#8A7E73]" : "text-white/80"}`}>{school.address}</p>
                    <p className={`text-xs font-bold mt-1 ${cfg.text} opacity-80`}>{cfg.label}</p>
                  </div>
                  {isSaving && (
                    <div className="absolute top-2 right-2">
                      <RefreshCw size={14} className={`animate-spin ${status === "pending" ? "text-[#C4BAAF]" : "text-white/60"}`} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Route picker modal */}
      {showRoutePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-[#E5E1DB]">
              <h3 className="font-bold text-[#3A322C]">Select Your Route</h3>
            </div>
            <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
              {routes.map(r => (
                <button key={r.id} onClick={() => pickRoute(r.id)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-bold transition-colors ${r.id === selectedRoute ? "bg-[#E8956D] text-[#3A322C]" : "bg-[#F4F3F0] text-[#3A322C] hover:bg-[#E8956D]/10"}`}>
                  Route {r.route_number} <span className="font-normal text-xs ml-2">({r.schools?.length || 0} schools)</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-[#E5E1DB]">
              <button onClick={() => setShowRoutePicker(false)} className="w-full border border-[#E5E1DB] rounded-xl py-3 text-sm font-bold text-[#5C5147]">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Exit modal */}
      {showExit && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 w-full max-w-xs text-center">
            <p className="font-bold text-[#3A322C] text-lg mb-2">Exit Delivery Mode?</p>
            <p className="text-sm text-[#8A7E73] mb-5">Your progress is auto-saved</p>
            <div className="flex gap-3">
              <button onClick={() => setShowExit(false)} className="flex-1 border border-[#E5E1DB] rounded-xl py-3 text-sm font-bold text-[#5C5147]">Stay</button>
              <button onClick={() => navigate("/delivery")} className="flex-1 bg-[#E8956D] rounded-xl py-3 text-sm font-bold text-[#3A322C]">Exit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
